import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseUrl } from "../../../../comman/config";
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/outline';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import toast, {Toaster} from 'react-hot-toast';

function CategorySequence() {
    const [categoryList, setCategoryList] = useState([]);
    const [sessionList, setSessionList] = useState([]);
    const [selectedSession, setSelectedSession] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    
    const navigate = useNavigate();

    useEffect(() => {
        getSessionList();
    }, []);

    useEffect(() => {
        if (selectedSession) {
            getCategoryList();
        }
    }, [selectedSession]);

    const getSessionList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/comman/list?table=session_master&condition=status=1`);
            setSessionList(response.data.data || []);
            
            // Auto-select first session if available
            if (response.data.data && response.data.data.length > 0) {
                setSelectedSession(response.data.data[0].id.toString());
            }
        } catch (error) {
            console.error("Error fetching session list:", error);
            setError("Failed to load sessions");
        }
    };

    const getCategoryList = async () => {
        if (!selectedSession) return;
        
        try {
            setLoading(true);
            // Get categories with their sequence for the selected session
            const response = await axios.get(`${baseUrl}/item/category/sequence/${selectedSession}`);
            
            // Sort categories by sequence
            const sortedCategories = (response.data.data || []).sort((a, b) => 
                (a.session_sequence || 999) - (b.session_sequence || 999)
            );
            
            setCategoryList(sortedCategories);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching category list:", error);
            setError("Failed to load categories");
            setLoading(false);
        }
    };

    const moveItem = (index, direction) => {
        if (
            (direction === 'up' && index === 0) || 
            (direction === 'down' && index === categoryList.length - 1)
        ) {
            return;
        }

        const newList = [...categoryList];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        
        // Swap items
        [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
        
        // Update sequence numbers
        newList.forEach((item, idx) => {
            item.session_sequence = idx + 1;
        });
        
        setCategoryList(newList);
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        
        const items = Array.from(categoryList);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        // Update sequence numbers
        items.forEach((item, idx) => {
            item.session_sequence = idx + 1;
        });
        
        setCategoryList(items);
    };

    const saveSequence = async () => {
        if (!selectedSession) {
            toast.error("Please select a session");
            return;
        }

        try {
            setLoading(true);
            setError('');
            setSuccess('');
            
            const sequenceData = categoryList.map(item => ({
                category_id: item.id,
                session_id: selectedSession,
                sequence: item.session_sequence
            }));
            
            await axios.post(`${baseUrl}/item/category/update_session_sequence`, { 
                sequenceData,
                session_id: selectedSession
            });
            
            toast.success("Sequence updated successfully");
            setLoading(false);
        } catch (error) {
            console.error("Error updating sequence:", error);
            toast.error("Failed to update sequence");
            setLoading(false);
        }
    };

    const cancelChanges = () => {
        navigate('/item_category/list');
    };

    const handleSessionChange = (e) => {
        setSelectedSession(e.target.value);
        setCategoryList([]); // Clear current categories
    };

    return (
        <div className="p-4">
            <Toaster position="top-center" reverseOrder={false} />
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">
                    Category Sequence by Session
                </h1>
            </div>
            
            {/* Session Selection */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Session:
                </label>
                <select
                    value={selectedSession}
                    onChange={handleSessionChange}
                    className="w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                >
                    <option value="">-- Select Session --</option>
                    {sessionList.map(session => (
                        <option key={session.id} value={session.id}>
                            {session.name}
                        </option>
                    ))}
                </select>
            </div>
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}
            
            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {success}
                </div>
            )}
            
            {selectedSession && (
                <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="categories">
                            {(provided) => (
                                <table 
                                    className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                                        <tr>
                                            <th scope="col" className="px-6 py-3">Sequence</th>
                                            <th scope="col" className="px-6 py-3">Category Name (Guj.)</th>
                                            <th scope="col" className="px-6 py-3">Category Name (Eng.)</th>
                                            <th scope="col" className="px-6 py-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {categoryList.map((item, index) => (
                                            <Draggable 
                                                key={item.id} 
                                                draggableId={item.id.toString()} 
                                                index={index}
                                            >
                                                {(provided) => (
                                                    <tr 
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                                    >
                                                        <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                                            {index + 1}
                                                        </td>
                                                        <td className="px-6 py-4">{item.name}</td>
                                                        <td className="px-6 py-4">{item.eng_name}</td>
                                                        <td className="px-6 py-4 flex">
                                                            <button 
                                                                onClick={() => moveItem(index, 'up')}
                                                                disabled={index === 0}
                                                                className={`mr-2 ${index === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                                                            >
                                                                <ArrowUpIcon className="w-5 h-5" />
                                                            </button>
                                                            <button 
                                                                onClick={() => moveItem(index, 'down')}
                                                                disabled={index === categoryList.length - 1}
                                                                className={`${index === categoryList.length - 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-800'}`}
                                                            >
                                                                <ArrowDownIcon className="w-5 h-5" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                </table>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            )}
            
            {!selectedSession && (
                <div className="text-center py-8 text-gray-500">
                    Please select a session to manage category sequence
                </div>
            )}
            
            <div className="flex justify-end mt-6 space-x-4">
                <button
                    onClick={cancelChanges}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring focus:ring-gray-200"
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    onClick={saveSequence}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                    disabled={loading || categoryList.length === 0 || !selectedSession}
                >
                    {loading ? 'Saving...' : 'Save Sequence'}
                </button>
            </div>
        </div>
    );
}

export default CategorySequence;